// Builds the code map of a folder's C# files: what each file declares, and which members call,
// create or read which (see walker.ts), without a compiler.
import type { TypeRef } from '../csharp/ast';
import { LineMap } from '../csharp/diagnostics';
import { parseCSharp, typeText } from '../csharp/parser';
import type { CodeEndpoint, CodeFile, CodeMap, CodeMember, CodeType } from './model';
import { SymbolTable, type ParsedCodeFile } from './symbols';
import { controllerEndpoints } from './endpoints';
import { findHandlers } from './handlers';
import { matchEndpoints } from './requests';
import { classifyProject, findTests, frameworksOf, looksLikeTestPath, projectInfos, type ProjectFile } from './tests';
import { Walker } from './walker';

export type { ParsedCodeFile } from './symbols';

/** The name C# gives the method holding a file's top-level statements. */
export const TOP_LEVEL_MAIN = '<Main>$';

/** Parses one file for the code map. Parsing is the slow part, so callers can keep the result per file. */
export function parseCodeFile(path: string, text: string): ParsedCodeFile {
  const clean = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  const { file, diagnostics } = parseCSharp(clean);
  // Top-level statements belong to a method of a `Program` class, as the compiler generates.
  const first = file.statements.find((s) => s.kind !== 'empty' && s.kind !== 'unknown') ?? file.statements[0];
  if (first) {
    const at = { start: first.span.start, end: first.span.start };
    const strings: TypeRef = { name: 'string', args: [], nullable: false, arrays: [1], span: at };
    file.classes.unshift({
      name: 'Program',
      fullName: 'Program',
      namespace: null,
      keyword: 'class',
      modifiers: ['partial'],
      baseTypes: [],
      attributes: [],
      methods: [
        {
          kind: 'method',
          name: TOP_LEVEL_MAIN,
          params: [{ name: 'args', type: strings }],
          body: file.statements,
          returnType: { name: 'void', args: [], nullable: false, arrays: [], span: at },
          typeParams: [],
          modifiers: ['static'],
          attributes: [],
          nameSpan: at,
          span: { start: first.span.start, end: file.statements.at(-1)!.span.end },
        },
      ],
      properties: [],
      fields: [],
      enumMembers: [],
      typeParams: [],
      primaryParams: null,
      baseArgs: null,
      returnType: null,
      outer: null,
      nameSpan: at,
      span: at,
    });
  }
  let lineCount = 1;
  for (let i = 0; i < clean.length; i++) if (clean.charCodeAt(i) === 10) lineCount++;
  return {
    path,
    text: clean,
    file,
    lines: new LineMap(clean),
    lineCount,
    problems: diagnostics.filter((d) => d.code.startsWith('CS_SKIPPED')).length,
  };
}

const MIGRATION_FILE = /(^|\/)\d{14}_[^/]+\.cs$/;

/**
 * Files a tool wrote rather than a person: EF migrations and snapshots, designer files, source
 * generator output. The code map leaves them out unless asked.
 */
export function isGeneratedCode(path: string, text?: string): boolean {
  const name = path.slice(path.lastIndexOf('/') + 1);
  if (/\.(Designer|g|g\.i|generated|AssemblyInfo|AssemblyAttributes)\.cs$/i.test(name) || /ModelSnapshot\.cs$/.test(name)) return true;
  if (name === 'AssemblyInfo.cs' || MIGRATION_FILE.test(path)) return true;
  return !!text && /^\s*\/\/\s*<auto-generated/.test(text);
}

/**
 * Whether a C# file belongs on the code map: not build output, and not in a hidden folder
 * (`.git`, `.vs`, or `.claude/worktrees`, which holds whole copies of the repository).
 */
export function isMappedPath(path: string): boolean {
  return path.endsWith('.cs') && !path.split('/').some((s) => s.startsWith('.') || s === 'bin' || s === 'obj' || s === 'node_modules');
}

/** The folder of the nearest `.csproj` above a file. */
export function projectFinder(projectPaths: string[]): (path: string) => string | null {
  const folders = projectPaths.map((p) => (p.includes('/') ? p.slice(0, p.lastIndexOf('/')) : '')).sort((a, b) => b.length - a.length);
  return (path) => folders.find((f) => f === '' || path.startsWith(`${f}/`)) ?? null;
}

export interface CodeMapOptions {
  /**
   * The `.csproj` files, to group files by project and share global usings: paths, or paths with
   * their text, to tell test projects (tests.ts) and read `<Using>` items.
   */
  projects?: (string | ProjectFile)[];
  /** `Directory.Build.props` files: properties and packages for the projects under them. */
  props?: ProjectFile[];
}

export function buildCodeMap(files: ParsedCodeFile[], options: CodeMapOptions = {}): CodeMap {
  const projectFiles = (options.projects ?? []).map((p) => (typeof p === 'string' ? { path: p, text: '' } : p));
  const projectOf = projectFinder(projectFiles.map((p) => p.path));
  const infos = projectInfos(projectFiles, options.props ?? []);
  const infoOf = new Map(infos.map((i) => [i.folder, i]));
  const infoOfFile = (path: string) => {
    const folder = projectOf(path);
    return folder === null ? undefined : infoOf.get(folder);
  };
  const table = new SymbolTable(files, projectOf, (folder) => {
    const info = folder === null ? undefined : infoOf.get(folder);
    return info ? [...info.props.flatMap((p) => p.facts.usings), ...info.facts.usings] : [];
  });
  const walker = new Walker(table);
  const byPath = new Map(files.map((f) => [f.path, f]));
  for (const sym of table.typeOrder) walker.walkType(sym, byPath);
  for (const m of table.members.values()) walker.walkMember(m, byPath.get(m.ctx.path)!);

  // Tests, then the projects that hold them.
  const tests = findTests(
    table,
    (path) => {
      const info = infoOfFile(path);
      return info ? frameworksOf([...info.facts.packages, ...info.props.flatMap((p) => p.facts.packages)]) : [];
    },
    new Set(walker.calls.map((c) => c.from)),
  );
  const withTests = new Set(tests.map((t) => t.file));
  const projects = infos.map((i) => classifyProject(i, tests.some((t) => projectOf(t.file) === i.folder)));
  const testProjects = new Set(projects.filter((p) => p.test).map((p) => p.folder));

  const members: CodeMember[] = [...table.members.values()].map((m) => ({
    id: m.id,
    type: m.owner.id,
    name: m.name === '.ctor' ? m.owner.name : m.name,
    kind: m.kind,
    signature: m.signature,
    valueType: m.valueType ? typeText(m.valueType) : null,
    modifiers: m.modifiers,
    file: m.ctx.path,
    line: m.line,
    body: m.hasBody,
  }));

  const types: CodeType[] = table.typeOrder.map((sym) => {
    const bases = table.basesOf(sym);
    return {
      id: sym.id,
      name: sym.name,
      kind: sym.kind,
      namespace: sym.namespace,
      typeParams: sym.typeParams,
      modifiers: [...new Set(sym.parts.flatMap((p) => p.cls.modifiers))],
      base: bases.base?.k === 'code' ? bases.base.sym.id : null,
      interfaces: bases.interfaces.flatMap((i) => (i.k === 'code' ? [i.sym.id] : [])),
      baseTypes: [...new Set(sym.parts.flatMap((p) => p.cls.baseTypes.map(typeText)))],
      parts: sym.parts.map((p) => ({ file: p.ctx.path, line: byPath.get(p.ctx.path)!.lines.at(p.cls.nameSpan.start).line })),
      outer: sym.outer?.id ?? null,
    };
  });

  // Each file lists its types and members in the order they are written.
  const filesOut: CodeFile[] = files.map((f) => {
    const fileTypes = new Set<string>();
    for (const sym of table.typeOrder) if (sym.parts.some((p) => p.ctx.path === f.path)) fileTypes.add(sym.id);
    const fileMembers = members.filter((m) => m.file === f.path).sort((a, b) => a.line - b.line);
    return {
      path: f.path,
      project: projectOf(f.path),
      test: withTests.has(f.path) || (projectOf(f.path) === null ? looksLikeTestPath(f.path) : testProjects.has(projectOf(f.path)!)),
      namespace: f.file.classes.find((c) => c.namespace)?.namespace ?? null,
      types: [...fileTypes],
      members: fileMembers.map((m) => m.id),
      lines: f.lineCount,
      problems: f.problems,
    };
  });

  const counted = walker.calls.filter((c) => c.kind !== 'access');
  const endpoints = [...walker.routedEndpoints(), ...controllerEndpoints(table, walker.endpointIds)].sort(byRoute);
  // Each HTTP request to the endpoints its method and URL fit.
  for (const r of walker.requests) Object.assign(r, matchEndpoints(endpoints, r.method, r.url));
  return {
    files: filesOut,
    types,
    members,
    calls: walker.calls,
    uses: [...walker.uses.values()],
    endpoints,
    projects,
    tests,
    handoffs: walker.handoffs,
    handlers: findHandlers(table),
    requests: walker.requests,
    stats: {
      files: files.length,
      types: types.length,
      members: members.length,
      exact: counted.filter((c) => c.certainty === 'exact').length,
      guess: counted.filter((c) => c.certainty === 'guess').length,
      external: walker.external,
      unresolved: walker.unresolved,
    },
  };
}

/** Endpoints in route order (ignoring case), then by method. */
function byRoute(a: CodeEndpoint, b: CodeEndpoint): number {
  const ra = a.route.toLowerCase();
  const rb = b.route.toLowerCase();
  if (ra !== rb) return ra < rb ? -1 : 1;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}
